import React, { FC, useMemo } from 'react'

import { Time } from '@/components/MainSidebar/Time'
import { UserCredits } from '@/components/MainSidebar/UserCredits'
import { focusIds, orientation } from '@/constants/focus'
import { icons } from '@/constants/icons'
import { useMenuItem } from '@/hooks/useMenuItem'
import { MainMenuPropsType } from '@/types/componentsPropsTypes'
import { MenuItemType } from '@/types/menuTypes'
import { getMainHomePath } from '@/utils/route'

import { useMainMenuFocus, useMainMenuItems } from './hooks'
import { MenuItem } from './MenuItem'
import { ButtonGroup, MainMenuContainer, Divider } from './styles'

const backNavigationMenuItem = {
  name: 'back',
  id: 'back',
  iconDirectPath: icons.navigation.backArrow,
}

export const MainMenu: FC<MainMenuPropsType> = ({
  focusIdPrefix,
  isSidebarFocused,
  isOpen,
  onBack,
  onMenuFocused,
  onMenuBlurred,
}) => {
  const focusId = focusIdPrefix + focusIds.MAIN_MENU

  const {
    mainMenuProfileItems,
    mainMenuHomeItems,
    mainMenuDefaultItems,
    mainMenuSettingsItems,
  } = useMainMenuItems()

  const mainMenuAllItems = useMemo(
    () => [
      ...mainMenuProfileItems,
      ...mainMenuHomeItems,
      ...mainMenuDefaultItems,
      ...mainMenuSettingsItems,
    ],
    [
      mainMenuProfileItems,
      mainMenuHomeItems,
      mainMenuDefaultItems,
      mainMenuSettingsItems,
    ],
  )

  /**
   * The Back button (if visible) takes the position of the Home menu item.
   */
  const defaultFocusChildRewrite = useMemo(() => {
    const homeItemIndex = mainMenuAllItems.findIndex(
      (el) => el.path === getMainHomePath(),
    )
    return onBack && (homeItemIndex === -1 ? undefined : homeItemIndex)
  }, [onBack, mainMenuAllItems])

  const [defaultFocusChild, onMove] = useMainMenuFocus(
    mainMenuAllItems,
    focusIdPrefix + focusIds.MAIN_MENU,
    defaultFocusChildRewrite,
  )

  const { checkIsActiveMenuItem, selectMenuItem } = useMenuItem()

  if (!isOpen) {
    return null // Hide MainMenu in favour of SubMenu
  }

  return (
    <>
      <MainMenuContainer
        elementType="nav"
        $subMenu={false}
        focusId={focusId}
        orientation={orientation.VERTICAL}
        defaultFocusChild={defaultFocusChild}
        onMove={onMove}
        onFocused={onMenuFocused}
        onBlurred={onMenuBlurred}
      >
        <UserCredits isCentered={!isSidebarFocused} />

        <ButtonGroup>
          {mainMenuProfileItems.map((menuItem: MenuItemType) => (
            <MenuItem
              key={menuItem.id}
              menuItem={menuItem}
              isSidebarFocused={isSidebarFocused}
              isActive={checkIsActiveMenuItem(menuItem)}
              onSelected={selectMenuItem}
              focusIdPrefix={focusIdPrefix}
            />
          ))}
        </ButtonGroup>

        <Divider />

        {onBack && (
          <>
            <ButtonGroup>
              <MenuItem
                menuItem={backNavigationMenuItem}
                isSidebarFocused={isSidebarFocused}
                isActive={false}
                onSelected={onBack}
                focusIdPrefix={focusIdPrefix}
              />
            </ButtonGroup>
            <Divider />
          </>
        )}

        <ButtonGroup>
          {mainMenuHomeItems.map((menuItem: MenuItemType) => (
            <MenuItem
              key={menuItem.id}
              menuItem={menuItem}
              isSidebarFocused={isSidebarFocused}
              isActive={checkIsActiveMenuItem(menuItem)}
              onSelected={selectMenuItem}
              focusIdPrefix={focusIdPrefix}
            />
          ))}
        </ButtonGroup>

        {mainMenuDefaultItems.length > 0 && (
          <>
            <Divider />

            <ButtonGroup>
              {mainMenuDefaultItems.map((menuItem: MenuItemType) => (
                <MenuItem
                  key={menuItem.id}
                  menuItem={menuItem}
                  isSidebarFocused={isSidebarFocused}
                  isActive={checkIsActiveMenuItem(menuItem)}
                  onSelected={selectMenuItem}
                  focusIdPrefix={focusIdPrefix}
                />
              ))}
            </ButtonGroup>
          </>
        )}

        {mainMenuSettingsItems.length > 0 && (
          <>
            <Divider />
            <ButtonGroup>
              {mainMenuSettingsItems.map((menuItem: MenuItemType) => (
                <MenuItem
                  key={menuItem.id}
                  menuItem={menuItem}
                  isSidebarFocused={isSidebarFocused}
                  isActive={checkIsActiveMenuItem(menuItem)}
                  onSelected={selectMenuItem}
                  focusIdPrefix={focusIdPrefix}
                />
              ))}
            </ButtonGroup>
          </>
        )}
      </MainMenuContainer>
      <Time isCentered={!isSidebarFocused} />
    </>
  )
}
